import callRpc from '~publish/legacy/utils/call-rpc'
import { logError } from '~publish/legacy/utils/logError'

import { SupportedEntity } from './nodes/LinkedInAnnotationInputElement'
import type { LinkedInAnnotationEntityDetails } from './nodes/LinkedInAnnotationInputElement'

// Map uses === semantics, so in order to compare by value, we're
// using a string hash rather than an object as the key
function hashCacheKey(profileId: string, url: string): string {
  return `${profileId}-${url}`
}

const supportedEntitiesString = Object.values(SupportedEntity).join('|')
export const LINKEDIN_ENTITY_URL_REGEX = new RegExp(
  `(?:(?:https|http)://(?:[a-z]{2,3}\\.)?linkedin.com/(?<entityType>${supportedEntitiesString})/)(?<vanityName>[a-zA-Z0-9-&'%._]*)`,
)
export const LINKEDIN_PROFILE_URL_REGEX =
  /(?:(?:https|http):\/\/(?:www.)?linkedin.com\/in\/)(?<vanityName>[a-zA-Z0-9-&'%._]*)/i

type LinkedinEntityLookUpResponse = {
  id: number
  vanityName: string
  localizedName: string
  entity: string
}

/**
 * Class for interacting with LinkedIn URL entities that can be converted to annotations
 *
 * Results are cached, and a same request done simultaneously will be
 * queued to return the same info as the already-running request.
 */

export const LinkedInAnnotation = {
  cache: new Map<string, LinkedinEntityLookUpResponse>(),

  isLinkedInEntityUrl(url: string): boolean {
    return LINKEDIN_ENTITY_URL_REGEX.test(url)
  },

  async parseLinkedInEntitiesUrlInText(
    urls: string[],
    profileId: string,
  ): Promise<LinkedInAnnotationEntityDetails[]> {
    return Promise.all(
      urls.filter(LinkedInAnnotation.isLinkedInEntityUrl).map(async (url) => {
        try {
          return LinkedInAnnotation.getLinkedInEntityDetails(url, profileId)
        } catch (e) {
          logError(e, { metaData: { url } })
        }
      }),
    ).then((entities) =>
      entities.filter((e): e is LinkedInAnnotationEntityDetails => !!e),
    )
  },

  // Returns a Promise that resolves with the full details of the entity as an object
  async getLinkedInEntityDetails(
    url: string,
    profileId: string,
  ): Promise<LinkedInAnnotationEntityDetails | undefined> {
    const regexResult = LINKEDIN_ENTITY_URL_REGEX.exec(url)
    const vanityName = regexResult?.groups?.vanityName

    const entityType = regexResult?.groups?.entityType
    const supportedEntityType = Object.values(SupportedEntity).find(
      (supportedEntity) => supportedEntity === entityType,
    )

    if (vanityName && supportedEntityType) {
      const entityDetails = await LinkedInAnnotation.lookUpEntity(
        decodeURI(vanityName),
        supportedEntityType,
        profileId,
      )

      if (entityDetails) {
        const { id, localizedName, entity } = entityDetails
        return {
          id,
          vanityName,

          localizedName,
          entity,
          link: url,
        }
      }
    }
  },

  // Returns a Promise that resolves with the basic details of the entity as an object
  lookUpEntity(
    vanityName: string,
    entityType: SupportedEntity,
    profileId: string,
  ): Promise<LinkedinEntityLookUpResponse | undefined> {
    const params = {
      vanityName,
      entityType,
      profile_id: profileId,
    }

    const cacheKey = hashCacheKey(vanityName, entityType ?? '')

    // If the result is cached, return it right away
    const cachedResult = LinkedInAnnotation.cache.get(cacheKey)
    if (cachedResult !== undefined) {
      return Promise.resolve(cachedResult)
    }

    const response: Promise<LinkedinEntityLookUpResponse | undefined> = callRpc(
      'composerApiProxy',
      {
        url: '1/profiles/linkedin_entity_lookup.json',
        args: params,
        HTTPMethod: 'GET',
      },
    ).then(
      (result: { success: boolean; data: LinkedinEntityLookUpResponse }) => {
        if (result.success) {
          LinkedInAnnotation.cache.set(cacheKey, result.data)
          return result.data
        }
        return undefined
      },
    )

    return response
  },
}
